import { Routes } from "@angular/router";
import { authenticationRoutes } from "@app/applications/authentication/authentication.routes";
import { loadTranslationResolver } from "@kernel/translations/resolvers/load-translations.resolver";
import { TRANSLATION_PATHS } from "./shared/general/translation/constants/translation.constant";
import { settingsRoutes } from "@app/applications/settings/settings.routes";
import { usersRoutes } from "./applications/users/users.routes";
import { authRoutes } from "./applications/auth/auth.routes";
import { AuthGuard } from "./shared/auth/guards/auth.guard";
import { companyHierarchyRoutes } from "./applications/company-hierarchy/company-hierarchy.routes";
import { EAccessType } from "./shared/auth/enums/auth.enum";
import { MainLayoutComponent } from "./shared/general/layouts/main-layout/main-layout.component";
import { AuthLayoutComponent } from "./shared/general/layouts/auth-layout/auth-layout.component";
import { assetsRoutes } from "./applications/assets/assets.routes";
import { dashboardRoutes } from "./applications/dashboard/dashboard.routes";
import { crmRoutes } from "./applications/crm/crm.routes";
import { dynamicFormsRoutes } from "./applications/dynamic-form/dynamic-forms.routes";
import { geoDataRoutes } from "./applications/geo-data/geo-data.routes";
import { fleetRoutes } from "./applications/fleet/fleet.routes";
import { homeRoutes } from "./applications/home/home.routes";
import { queueRoutes } from "./applications/queue/queue.routes";
import { QUEUE_STATISTICS_BASE } from "./applications/queue/domains/statistics/constants/queue-statistics-routes.constant";
import { movementsManagementRoutes } from "./applications/movements-management/movements-management.routes";
import { revenueManagementRoutes } from "./applications/revenue-management/revenue-management.routes";
import { chatRoutes } from "./applications/chat/chat.routes";

export const routes: Routes = [
  // The queue statistics dashboard is what the application opens on, but it lives
  // at a path that says so. A redirect rather than mounting the domain on the empty
  // path: that put a parent route in front of every other section's URL, which only
  // fell through because its children were exact-matched.
  //
  // `pathMatch: "full"` so this claims `/` alone. It sits above the main layout so
  // no guard or resolver runs for what is only a redirect — the target is behind the
  // same `AuthGuard` either way. The catch-all at the bottom redirects to `""` and
  // so lands here too.
  { path: "", pathMatch: "full", redirectTo: QUEUE_STATISTICS_BASE },
  {
    path: "",
    component: MainLayoutComponent,
    resolve: {
      transition: loadTranslationResolver(TRANSLATION_PATHS.ROOT),
    },
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        allowAccessWithoutSession: false,
        // Allow both full access and verification access
        accessType: [EAccessType.FullAccess],
        // For verification access, we want to redirect appropriately
        redirectTo: "/auth/login",
      },
    },
    children: [
      // Where the dashboard's permission guard sends a user who cannot see it.
      // Needs no permission of its own beyond the layout's — that is the point of
      // it, and why the guard cannot simply send them back to `/`.
      {
        path: "home",
        children: [...homeRoutes],
      },
      // TODO: I think this should be handled in a different way.
      {
        path: "settings",
        children: [...authenticationRoutes, ...usersRoutes, ...settingsRoutes, ...companyHierarchyRoutes, ...dynamicFormsRoutes, ...geoDataRoutes],
      },
      {
        path: "assets",
        children: [...assetsRoutes],
      },
      {
        path: "crm",
        children: [...crmRoutes],
      },
      {
        path: "dashboard",
        children: [...dashboardRoutes],
      },
      {
        path: "fleet",
        children: [...fleetRoutes],
      },
      {
        path: "movements-management",
        children: [...movementsManagementRoutes],
      },
      {
        path: "revenue-management",
        children: [...revenueManagementRoutes],
      },
      {
        path: "chat",
        children: [...chatRoutes],
      },
    ],
  },
  {
    path: "auth",
    component: AuthLayoutComponent,
    resolve: {
      translation: loadTranslationResolver([TRANSLATION_PATHS.ROOT, TRANSLATION_PATHS.AUTH]),
    },
    children: [...authRoutes],
  },
  {
    path: "queue",
    resolve: {
      translation: loadTranslationResolver([TRANSLATION_PATHS.ROOT]),
    },
    canActivate: [AuthGuard],
    data: {
      authGuardOptions: {
        allowAccessWithoutSession: false,
        // Allow both full access and verification access
        accessType: [EAccessType.FullAccess],
        // For verification access, we want to redirect appropriately
        redirectTo: "/auth/login",
      },
    },
    children: [...queueRoutes],
  },
  {
    path: "verification-result",
    loadComponent: () =>
      import("@app/shared/general/contact-verification/components/verification-redirect/verification-redirect.component").then(
        m => m.VerificationRedirectComponent
      ),
    resolve: {
      translation: loadTranslationResolver([TRANSLATION_PATHS.ROOT, TRANSLATION_PATHS.CRM.SHARED]),
    },
  },
  // Straight to the dashboard rather than via `""`. Bouncing through the root
  // worked — redirects chain — but a wildcard that lands somewhere in one hop is
  // one less thing to reason about when the root's own target changes.
  { path: "**", redirectTo: QUEUE_STATISTICS_BASE, pathMatch: "full" },
];
